import React, { Fragment, useEffect } from "react";
import { getCurrentContextEmailProps } from "@utils";
import { useAppDispatch, useAppSelector } from "@store";
import {
  copyRephraseResult,
  currentQuestionSelector,
  getQuestionnaire,
  rephrase,
  savedResultsSelector,
  sendFeedback,
} from "@store/messageWizard";
import { useMixpanel } from "@taskpane/context";
import { Tooltip, TooltipSize } from "@taskpane/components";
import { copyToClipboard } from "./utils";
import {
  CopyFilledIcon,
  CopyIcon,
  DislikeFilledIcon,
  DislikeIcon,
  LikeFilledIcon,
  LikeIcon,
} from "./icons";
import { WizardAnswer, WizardAuthorityZoneResult, WizardMessage } from "./components";
import css from "./Wizard.module.scss";

export interface WizardProps {
  isNewDraft: boolean;
}

const Wizard: React.FC<WizardProps> = ({ isNewDraft }) => {
  const dispatch = useAppDispatch();

  const isCompleted = useAppSelector((state) => state.messageWizard.isCompleted);
  const selectedBranch = useAppSelector((state) => state.simulator.selectedBranch);
  const questions = useAppSelector((state) => state.messageWizard.questions);
  const savedResults = useAppSelector((state) => state.messageWizard.savedResults);
  const rephraseResult = useAppSelector((state) => state.messageWizard.rephraseResult);
  const currentQuestion = useAppSelector(currentQuestionSelector);
  const extendedSavedResults = useAppSelector(savedResultsSelector);
  const isFeedbackLoading = useAppSelector((state) => state.messageWizard.isLoading.feedback);
  const isRephraseLoading = useAppSelector((state) => state.messageWizard.isLoading.rephrase);
  const isTextCopied = useAppSelector((state) => state.messageWizard.isTextCopied);
  const isPositiveFeedback = useAppSelector((state) => state.messageWizard.isPositiveFeedback);
  const isQuestionnaireLoading = useAppSelector(
    (state) => state.messageWizard.isLoading.questionnaire
  );

  const { trackEvent } = useMixpanel();

  const { branchId, draft } = selectedBranch || {};

  const attrs = {
    container: {
      className: css.container,
    },
    header: {
      className: css.header,
    },
    initialMessage: {
      content: "Hi! Answer a few questions to generate your message",
    },
    completedMessage: {
      content: (
        <>
          Thanks for your responses!
          <br />
          AI is preparing a reply optimized for the right Authority Zone positioning.
        </>
      ),
    },
    rephraseMessage: {
      isLoading: isRephraseLoading || !rephraseResult?.newText,
      content: rephraseResult?.newText,
      shoulScrollIntoView: true,
    },
    wizardAuthorityZoneResult: {
      selectedBranch,
      currentScore: rephraseResult?.scoreInBranch,
    },
    currentQuestion: {
      isLoading: isQuestionnaireLoading || !currentQuestion,
      content: `${currentQuestion?.index + 1}. ${currentQuestion?.text}`,
      shoulScrollIntoView: true,
    },
    footer: {
      className: css.footer,
    },
    copyButtonTooltip: {
      size: TooltipSize.XS,
      arrow: false,
      autoPosition: true,
      positionOffset: 4,
      text: "Copy",
    },
    copyButton: {
      type: "button" as const,
      className: css.footerButton,
      onClick: () => {
        if (rephraseResult?.newText) {
          trackEvent("simulatorScreen.wizardCopyTextClick");
          dispatch(copyRephraseResult());
          copyToClipboard(rephraseResult.newText);
        }
      },
    },
    likeButtonTooltip: {
      size: TooltipSize.XS,
      arrow: false,
      autoPosition: true,
      positionOffset: 4,
      text: "Mark as Helpful",
    },
    likeButton: {
      type: "button" as const,
      className: css.footerButton,
      disabled: isFeedbackLoading || isPositiveFeedback,
      onClick: async () => {
        const { draftText } = await getCurrentContextEmailProps();

        if (draft && draftText) {
          trackEvent("simulatorScreen.wizardPositiveFeedbackClick");
          dispatch(
            sendFeedback({
              draft,
              isPositive: true,
              currentText: draftText,
              generatedText: rephraseResult?.newText || "",
            })
          );
        }
      },
    },
    dislikeButtonTooltip: {
      size: TooltipSize.XS,
      arrow: false,
      autoPosition: true,
      positionOffset: 4,
      text: "Mark as Not Helpful",
    },
    dislikeButton: {
      type: "button" as const,
      className: css.footerButton,
      disabled: isFeedbackLoading || isPositiveFeedback === false,
      onClick: async () => {
        const { draftText } = await getCurrentContextEmailProps();

        if (draft && draftText) {
          trackEvent("simulatorScreen.wizardNegativeFeedbackClick");
          dispatch(
            sendFeedback({
              draft,
              isPositive: false,
              currentText: draftText,
              generatedText: rephraseResult?.newText || "",
            })
          );
        }
      },
    },
  };

  const renderWizardHistory = () =>
    extendedSavedResults.map(({ question, answer }, index) => {
      const attrs = {
        question: {
          content: `${index + 1}. ${question.text}`,
        },
        answer: {
          options: question.options.map((option) => ({
            label: option.text,
            isSelected: option.optionId === answer.optionId,
          })),
        },
      };

      return (
        <Fragment key={index}>
          <WizardMessage {...attrs.question} />
          <WizardAnswer {...attrs.answer} />
        </Fragment>
      );
    });

  const renderCurrentConversation = () => {
    if (isCompleted) {
      const wizardMessageAuthorityZoneResult = {
        content: <WizardAuthorityZoneResult {...attrs.wizardAuthorityZoneResult} />,
      };

      return (
        <>
          <WizardMessage {...attrs.completedMessage} />
          {rephraseResult ? <WizardMessage {...wizardMessageAuthorityZoneResult} /> : null}
          <WizardMessage {...attrs.rephraseMessage} />
        </>
      );
    }

    return <WizardMessage {...attrs.currentQuestion} />;
  };

  const renderFooter = () => {
    if (!rephraseResult) {
      return null;
    }

    return (
      <div {...attrs.footer}>
        <Tooltip {...attrs.copyButtonTooltip}>
          <button {...attrs.copyButton}>{isTextCopied ? <CopyFilledIcon /> : <CopyIcon />}</button>
        </Tooltip>
        {isPositiveFeedback === false ? null : (
          <Tooltip {...attrs.likeButtonTooltip}>
            <button {...attrs.likeButton}>
              {isPositiveFeedback ? <LikeFilledIcon /> : <LikeIcon />}
            </button>
          </Tooltip>
        )}
        {isPositiveFeedback ? null : (
          <Tooltip {...attrs.dislikeButtonTooltip}>
            <button {...attrs.dislikeButton}>
              {isPositiveFeedback === false ? <DislikeFilledIcon /> : <DislikeIcon />}
            </button>
          </Tooltip>
        )}
      </div>
    );
  };

  useEffect(() => {
    let request: Promise<unknown> & {
      abort: () => void;
    };

    if (branchId && draft && !isNewDraft && !questions.length) {
      request = dispatch(
        getQuestionnaire({
          branchId,
          platformDraftId: draft.platformDraftId,
          platformThreadId: draft.platformThreadId,
        })
      );
    }

    return () => {
      request?.abort();
    };
  }, [dispatch, branchId, draft, isNewDraft, questions]);

  useEffect(() => {
    (async () => {
      const { draftText } = await getCurrentContextEmailProps();

      if (isCompleted && draftText && draft && !rephraseResult) {
        dispatch(rephrase({ currentText: draftText, draft, savedResults }));
      }
    })();
  }, [dispatch, isCompleted, draft, rephraseResult]);

  return (
    <div {...attrs.container}>
      <div {...attrs.header}>
        {!isNewDraft ? (
          <span>Answer a few question to generate reply.</span>
        ) : (
          <span>The Wizard is only available when replying to a message.</span>
        )}
      </div>
      {!isNewDraft ? (
        <>
          <WizardMessage {...attrs.initialMessage} />
          {renderWizardHistory()}
          {renderCurrentConversation()}
          {renderFooter()}
        </>
      ) : null}
    </div>
  );
};

export default Wizard;
